/**
 * PSIFY IMMERSIVE — Coregrafia capitolelor (GSAP ScrollTrigger)
 * Fiecare capitol își are timeline-ul propriu; totul e idempotent
 * și tolerant la absența secțiunilor.
 */
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/* ---------- utilitare ---------- */

// Sparge textul în cuvinte pentru reveal (păstrează accesibilitatea via aria-label)
function splitWords(el) {
  const text = el.textContent.trim()
  el.setAttribute('aria-label', text)
  el.classList.add('psi-split-words')
  el.innerHTML = text
    .split(/\s+/)
    .map((w) => `<span class="psi-word" aria-hidden="true"><span>${w}</span></span>`)
    .join(' ')
  return el.querySelectorAll('.psi-word > span')
}

/* ---------- Cap. 1: HERO ---------- */

function initHero(onProgress) {
  const hero = document.querySelector('.psi-hero')
  if (!hero) return

  const title = hero.querySelector('.psi-hero-title')
  const tl = gsap.timeline({ delay: 0.15 })

  if (title) {
    const words = splitWords(title)
    tl.to(words, {
      y: 0,
      duration: 1.1,
      ease: 'power4.out',
      stagger: 0.07
    })
  }

  tl.to(hero.querySelector('.psi-hero-eyebrow'), { opacity: 1, duration: 0.8, ease: 'power2.out' }, 0.2)
    .to(hero.querySelector('.psi-hero-sub'), { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out' }, 0.55)
    .to(hero.querySelector('.psi-hero-actions'), { opacity: 1, duration: 0.9, ease: 'power2.out' }, 0.75)

  // parallax blând la scroll + estompare
  gsap.to(hero.querySelector('.psi-container'), {
    yPercent: -12,
    opacity: 0.25,
    ease: 'none',
    scrollTrigger: {
      trigger: hero,
      start: 'top top',
      end: 'bottom top',
      scrub: true
    }
  })

  return tl
}

/* ---------- Cap. 2: TRANSFORMAREA (pinned) ---------- */

function initTransform(onProgress, onActiveChange) {
  const section = document.querySelector('.psi-transform')
  if (!section) return

  const stage = section.querySelector('.psi-transform-stage')
  const phrases = gsap.utils.toArray(section.querySelectorAll('.psi-transform-phrase'))
  if (!stage || !phrases.length) return

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: '+=300%',
      pin: stage,
      scrub: 0.6,
      onToggle(self) {
        // experiența cu text începe/se termină (pt. soundscape)
        if (onActiveChange) onActiveChange(self.isActive)
      },
      onUpdate(self) {
        // progresul global al transformării → particulele WebGL + sunet
        if (onProgress) onProgress(self.progress)
      }
    }
  })

  phrases.forEach((phrase, i) => {
    // prima frază intră mai repede (0.55 în loc de 1): la pin, hero-ul e deja estompat și ecranul rămânea gol
    // aproape o jumătate de derulare (23.09.2026); reveal-ul rămâne, doar golul se scurtează
    tl.fromTo(phrase,
      { opacity: 0, y: 60, filter: 'blur(8px)' },
      { opacity: 1, y: 0, filter: 'blur(0px)', duration: i === 0 ? 0.55 : 1, ease: 'power2.out' }
    )
    tl.to(phrase, { opacity: 1, duration: i === 0 ? 1.1 : 0.8 }) // pauză de citit
    if (i < phrases.length - 1) {
      tl.to(phrase, { opacity: 0, y: -50, filter: 'blur(8px)', duration: 1, ease: 'power2.in' })
    }
  })
}

/* ---------- Reveals generice pe capitole ---------- */

function initReveals() {
  // headere de secțiune
  gsap.utils.toArray('.psi-reveal').forEach((el) => {
    gsap.fromTo(el,
      { opacity: 0, y: 48 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: { trigger: el, start: 'top 85%' }
      }
    )
  })

  // grupuri cu stagger (pași, servicii, voci)
  gsap.utils.toArray('[data-psi-stagger]').forEach((group) => {
    const items = Array.from(group.children)
    if (!items.length) return
    gsap.fromTo(items,
      { opacity: 0, y: 56 },
      {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.12,
        scrollTrigger: { trigger: group, start: 'top 82%' }
      }
    )
  })
}

/* ---------- Cap. 4: GALERIA TERAPEUȚILOR (drag orizontal) ---------- */

function initDoctorsGallery() {
  const viewport = document.querySelector('.psi-doctors-viewport')
  const track = document.querySelector('.psi-doctors-track')
  if (!viewport || !track) return

  // pe touch, galeria folosește scroll orizontal NATIV (CSS overflow-x + snap);
  // transform-urile GSAP ar intra în conflict cu el
  if (window.matchMedia('(pointer: coarse)').matches) return

  const maxScroll = () => Math.max(0, track.scrollWidth - viewport.clientWidth)
  let currentX = 0

  const setX = gsap.quickTo(track, 'x', { duration: 0.8, ease: 'power3.out' })

  // deplasare legată de scroll vertical — galeria alunecă ușor
  gsap.to(track, {
    x: () => -Math.min(maxScroll() * 0.35, 320),
    ease: 'none',
    scrollTrigger: {
      trigger: viewport,
      start: 'top bottom',
      end: 'bottom top',
      scrub: 1,
      invalidateOnRefresh: true
    }
  })

  // drag manual — devine drag doar după ce cursorul s-a mișcat vizibil; un click simplu rămâne click.
  // Înainte, setPointerCapture + .is-dragging (pointer-events: none pe carduri) porneau la orice apăsare, deci
  // click-ul pe cardul medicului nu ajungea niciodată la link (owner 23.09.2026: „nu pot da clic pe cardul medicului”)
  const DRAG_THRESHOLD = 6
  let isDown = false
  let isDragging = false
  let startX = 0
  let startCurrent = 0

  const getX = () => gsap.getProperty(track, 'x')

  viewport.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return
    isDown = true
    isDragging = false
    startX = e.clientX
    startCurrent = getX()
  })

  viewport.addEventListener('pointermove', (e) => {
    if (!isDown) return
    const dx = e.clientX - startX
    if (!isDragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return
      isDragging = true
      viewport.classList.add('is-dragging')
      viewport.setPointerCapture(e.pointerId)
    }
    currentX = gsap.utils.clamp(-maxScroll(), 40, startCurrent + dx * 1.4)
    setX(currentX)
  })

  // click-ul care încheie un drag real nu navighează; fereastra e scurtă (ms), ca următorul click să meargă mereu
  let suppressClickUntil = 0

  const release = () => {
    isDown = false
    if (isDragging) {
      isDragging = false
      suppressClickUntil = performance.now() + 120
      requestAnimationFrame(() => viewport.classList.remove('is-dragging'))
    }
  }
  viewport.addEventListener('pointerup', release)
  viewport.addEventListener('pointercancel', release)

  viewport.addEventListener('click', (e) => {
    if (performance.now() < suppressClickUntil) {
      e.preventDefault()
      e.stopPropagation()
    }
  }, true)
}

/* ---------- Cap. 6: hover magnetic pe carduri servicii ---------- */

function initServiceCards() {
  document.querySelectorAll('.psi-service-card').forEach((card) => {
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect()
      card.style.setProperty('--mx', `${e.clientX - r.left}px`)
      card.style.setProperty('--my', `${e.clientY - r.top}px`)

      // înclinare magnetică discretă
      const dx = (e.clientX - r.left) / r.width - 0.5
      const dy = (e.clientY - r.top) / r.height - 0.5
      gsap.to(card, { rotateY: dx * 4, rotateX: -dy * 4, transformPerspective: 700, duration: 0.4, ease: 'power2.out' })
    })

    card.addEventListener('pointerleave', () => {
      gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.7, ease: 'elastic.out(1, 0.6)' })
    })
  })
}

/* ---------- Drumul inelului de particule prin pagină ----------
   A ieșit pe 07.10.2026, odată cu prima pagină v2: particulele se liniștesc și dispar pe manifest și nu se mai întorc
   (owner, 06.10: „nu mai avem nevoie să se unească în cerc”); de atunci le conduce home.js (PsifyWebGL.setProgress /
   setRing), nu ScrollTrigger. Până atunci inelul se retrăgea în fundal sub Cap. 3–6, se așeza pe cercul respirației și
   revenea sub CTA-ul final. */

/* ---------- Cap. 7: RESPIRAȚIA — ghidaj 4-7-8 sincronizat ---------- */

function initBreath(onBreathSync) {
  const section = document.querySelector('.psi-breath')
  if (!section) return

  const word = section.querySelector('.psi-breath-word')
  if (!word) return

  // faza curentă a cercului CSS (keyframes psiBreath478, ciclu 19 s), ca textul și shader-ul să pornească
  // din același punct al respirației — înainte, fiecare avea ceasul lui și „Inspiră” putea cădea pe expir
  const circle = section.querySelector('.psi-breath-circle')
  const cssPhaseSeconds = () => {
    if (!circle || typeof circle.getAnimations !== 'function') return 0
    const anim = circle.getAnimations({ subtree: true }).find((a) => a.animationName === 'psiBreath478')
    if (!anim || typeof anim.currentTime !== 'number') return 0
    return (anim.currentTime / 1000) % 19
  }
  const playInSync = () => {
    const t = cssPhaseSeconds()
    cycle.play(t)
    if (onBreathSync) onBreathSync(t)
  }

  const IN = word.dataset.in || 'Inspiră'
  const HOLD = word.dataset.hold || 'Ține'
  const OUT = word.dataset.out || 'Expiră încet'

  // ciclu 19s: 4 inspir / 7 ține / 8 expir — sincron cu keyframes CSS
  const cycle = gsap.timeline({ repeat: -1, paused: true })
  cycle
    .call(() => { word.textContent = IN })
    .to(word, { opacity: 1, duration: 0.6 }, 0)
    .to(word, { opacity: 0.85, duration: 3.4 })
    .call(() => { word.textContent = HOLD })
    .to(word, { opacity: 1, duration: 0.5 })
    .to(word, { opacity: 0.85, duration: 6.5 })
    .call(() => { word.textContent = OUT })
    .to(word, { opacity: 1, duration: 0.5 })
    .to(word, { opacity: 0.85, duration: 7.5 })

  ScrollTrigger.create({
    trigger: section,
    start: 'top 60%',
    end: 'bottom 40%',
    onEnter: playInSync,
    onEnterBack: playInSync,
    onLeave: () => cycle.pause(),
    onLeaveBack: () => cycle.pause()
  })
}

/* ---------- Nav: glass la scroll + inversare pe capitole întunecate ---------- */

function initNav() {
  const nav = document.querySelector('.psi-nav')
  if (!nav) return

  ScrollTrigger.create({
    start: 80,
    onUpdate: (self) => nav.classList.toggle('is-scrolled', self.scroll() > 80)
  })

  // pe secțiuni întunecate, nav trece pe crem
  gsap.utils.toArray('.psi-voices, .psi-footer').forEach((dark) => {
    ScrollTrigger.create({
      trigger: dark,
      start: 'top 60px',
      end: 'bottom 60px',
      onToggle: (self) => nav.classList.toggle('is-inverse', self.isActive)
    })
  })
}

/* ---------- API public ---------- */

export function initChapters({ onTransformProgress, onTransformActive, onBreathSync } = {}) {
  initNav()
  initHero()
  initTransform(onTransformProgress, onTransformActive)
  initReveals()
  initDoctorsGallery()
  initServiceCards()
  initBreath(onBreathSync)

  // recalculare după încărcarea imaginilor
  window.addEventListener('load', () => ScrollTrigger.refresh())
}

export { gsap, ScrollTrigger }
